import { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  useHabitStatLeaderboard,
  useLeaderboard,
  type LeaderboardTab,
} from "@/shared/hooks";
import { isMembershipRequiredError } from "@/shared/api/client";
import {
  LEADERBOARD_TAB_DESCRIPTIONS,
  LEADERBOARD_TAB_TEXTS,
  leaderboardMetricLabel,
} from "@/shared/texts/leaderboard";
import { Avatar } from "@/shared/ui/Avatar";
import { EmptyState } from "@/shared/ui/EmptyState";
import { HabitNav } from "@/shared/ui/HabitNav";
import { PageHeader } from "@/shared/ui/PageHeader";
import { ScreenLayout } from "@/shared/ui/ScreenLayout";
import { Skeleton } from "@/shared/ui/Skeleton";
import { Tabs } from "@/shared/ui/Tabs";
import type { LeaderboardEntry, StatLeaderboardEntry } from "@/shared/types";

// Pravki §7 v3.2 + Phase 3 v2 Task 3.9: единые лейблы со страницей "Рейтинг"
// (Серии/Охотники/Лентяи + Характеристика). Тексты — в shared/texts/leaderboard.
const TAB_ORDER: LeaderboardTab[] = ["streak", "catches", "shame", "stat"];
const TABS = TAB_ORDER.map((id) => ({ id, ...LEADERBOARD_TAB_TEXTS[id] }));

const EMPTY_STATES: Record<LeaderboardTab, { icon: string; title: string; description: string }> = {
  streak: {
    icon: "🔥",
    // Этап 5: метрика таба — ТЕКУЩАЯ серия. Пустой список значит, что у
    // участников нет ни одной текущей серии (раньше это означало «никто
    // ни разу не отметился»).
    title: "Пока ни у кого нет серии",
    description: "Сделай чек-ин — и начнётся твоя серия дней подряд.",
  },
  catches: {
    icon: "🎯",
    title: "Пока никто никого не поймал",
    description: "Будь первым — поймай нарушителя, пока он не отметился.",
  },
  shame: {
    icon: "😴",
    title: "Пока все молодцы",
    description: "Пропусти чек-ин — и ты автоматически попадёшь сюда.",
  },
  stat: {
    icon: "📊",
    title: "Характеристика не активирована",
    description:
      "Админ клуба ещё не выбрал характеристику. Попросите его настроить в Habit Settings.",
  },
};

const VALID_TABS = new Set<LeaderboardTab>(["streak", "catches", "shame", "stat"]);

// Phase 3 v2 Task 3.9: LeaderboardEntry.metric_value и
// StatLeaderboardEntry.value — разные имена для одной семантики
// (sortable score в строке рейтинга). Один <LeaderboardRow> используется
// для всех табов, поэтому нормализуем через type-narrowing.
function rowMetricValue(row: LeaderboardEntry | StatLeaderboardEntry): number {
  return "metric_value" in row ? row.metric_value : row.value;
}

export function LeaderboardPage() {
  const { habitId } = useParams<{ habitId: string }>();
  const [searchParams] = useSearchParams();
  // Аккордеон на /leaderboards передаёт активный tab через ?tab=shame
  // (или catches/streak/stat). Если параметр невалиден — default streak.
  const initialTab = (() => {
    const raw = searchParams.get("tab");
    if (raw && VALID_TABS.has(raw as LeaderboardTab)) {
      return raw as LeaderboardTab;
    }
    return "streak" as LeaderboardTab;
  })();
  const [tab, setTab] = useState<LeaderboardTab>(initialTab);

  // ⚠ Phase 3 v2 Task 3.9 review: оба хука гейтятся по активному табу
  // через options.enabled — иначе при просмотре «Серии» всё равно шлётся
  // запрос на /habits/{id}/leaderboard (stat), и наоборот.
  const isStatTab = tab === "stat";
  const streakCatchesShame = useLeaderboard(habitId, tab, {
    enabled: !isStatTab,
  });
  const statLeaderboard = useHabitStatLeaderboard(habitId, {
    enabled: isStatTab,
  });
  const { data, isLoading, isError, error } = isStatTab
    ? statLeaderboard
    : streakCatchesShame;

  return (
    <ScreenLayout>
      <PageHeader title="Лидеры клуба" back backTo="/leaderboards" />

      <Tabs tabs={TABS} active={tab} onChange={setTab} />

      <p className="text-center text-xs text-muted">{LEADERBOARD_TAB_DESCRIPTIONS[tab]}</p>

      {isLoading && <Skeleton className="h-14 w-full" rows={5} />}

      {isError && (
        isMembershipRequiredError(error) ? (
          <EmptyState
            icon="🔒"
            title="Вступи в клуб, чтобы увидеть лидерборд"
            description="Рейтинг клуба доступен только его участникам."
          />
        ) : (
          <EmptyState icon="⚠️" title="Не удалось загрузить" description={String(error)} />
        )
      )}

      {!isLoading && !isError && (data?.items.length ?? 0) === 0 && (
        <EmptyState
          icon={EMPTY_STATES[tab].icon}
          title={EMPTY_STATES[tab].title}
          description={EMPTY_STATES[tab].description}
        />
      )}

      {!isLoading && !isError && (data?.items.length ?? 0) > 0 && (
        <>
          {data?.total != null && (
            <p className="text-center text-xs text-muted">
              Показаны топ-100 из {data.total}
            </p>
          )}
          <ol className="space-y-1.5">
            {data!.items.map((row) => (
              <li key={row.membership_id}>
                <LeaderboardRow
                  row={row}
                  metricLabel={leaderboardMetricLabel(tab, rowMetricValue(row))}
                />
              </li>
            ))}
          </ol>
        </>
      )}

      <HabitNav habitId={habitId!} />
    </ScreenLayout>
  );
}

function LeaderboardRow({
  row,
  metricLabel,
}: {
  // Phase 3 v2 Task 3.9: stat-таб возвращает StatLeaderboardEntry
  // (нет photo_url, нет breakdown). Используем union + "in" guard.
  row: LeaderboardEntry | StatLeaderboardEntry;
  metricLabel: string;
}) {
  // photo_url приходит как "/api/v1/users/{id}/photo" — backend отдаёт
  // FileResponse. Браузер сам рендерит <img> (не usePhotoBlob).
  // StatLeaderboardEntry не имеет photo_url → null.
  const photoSrc =
    "photo_url" in row && row.photo_url
      ? new URL(row.photo_url, window.location.origin).toString()
      : null;
  // ❄ для frozen stat-участников (только StatLeaderboardEntry имеет is_frozen).
  const isFrozen = "is_frozen" in row && row.is_frozen;
  return (
    <article
      className={`flex items-center gap-3 rounded-card border border-white/10 bg-surface/60 px-2 py-2 sm:gap-4 sm:px-3 sm:py-2.5 ${
        isFrozen ? "opacity-60" : ""
      }`}
    >
      <Avatar
        src={photoSrc}
        fallback={row.first_name}
        size="xs"
        loading="eager"
        ring
        className="mr-2 sm:mr-3"
      />
      <span className="min-w-0 truncate text-xs font-medium text-text sm:text-sm">
        {isFrozen ? "❄ " : ""}
        {row.first_name}
      </span>
      <span className="border-l border-white/10 pl-2 text-xs font-bold tabular-nums text-primary sm:pl-3 sm:text-sm whitespace-nowrap">
        {metricLabel}
      </span>
      {/* breakdown есть только у LeaderboardEntry (streak/catches/shame),
          не у StatLeaderboardEntry.
          📅 — всего чек-инов за всё время, 🔥 — текущая серия дней подряд
          (этап 5: реальное значение, раньше было захардкожено 0). На табе
          «Серии» 🔥 совпадает с основной метрикой — breakdown показывает
          одинаковый набор метрик на всех табах. */}
      {"breakdown" in row && row.breakdown && (
        <span className="border-l border-white/10 pl-2 text-[10px] tabular-nums text-muted sm:pl-3 sm:text-xs whitespace-nowrap">
          📅 {row.breakdown.checkin_count}
          {" · "}🔥 {row.breakdown.streak_days}
          {" · "}🎯 {row.breakdown.catches_count}
          {" · "}😴 {row.breakdown.penalties_count}
        </span>
      )}
    </article>
  );
}
